"use client";

import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { resolveProfileLink } from "@/lib/link-types";
import type { MarkdownBlockNode, MarkdownSection, ProfileLink, ProfileRenderModel } from "@/lib/types";
import { MarkdownBody } from "../MarkdownBody";
import { ProfileLinkItem, ThemeAttribution, type ThemeRendererProps } from "./theme-primitives";

type TerminalFile = {
  aliases: string[];
  name: string;
  section: MarkdownSection;
};

export type TerminalCommandResult =
  | { kind: "clear" }
  | { kind: "markdown"; nodes: MarkdownBlockNode[] }
  | { kind: "links-tree"; links: ProfileLink[] }
  | { kind: "lines"; lines: string[]; tone?: "error" | "status" }
  | { kind: "motd"; handle: string }
  | { files: string[]; kind: "root-tree"; links: ProfileLink[] };

type TerminalExecution = {
  link?: ProfileLink;
  result: TerminalCommandResult;
};

type TerminalEntry = {
  command: string;
  id: number;
  result: TerminalCommandResult;
};

type TerminalCommandContext = {
  history: string[];
  links: ProfileLink[];
  model: ProfileRenderModel;
};

const COMMANDS = [
  "help",
  "whoami",
  "intro",
  "tags",
  "ls",
  "tree",
  "tree .",
  "tree -a ./links",
  "links",
  "open ",
  "pwd",
  "uname -a",
  "cat /etc/motd",
  "history",
  "clear"
];

function normalizeArgument(value: string) {
  const trimmed = value.trim();
  if (trimmed.length >= 2) {
    const first = trimmed[0];
    const last = trimmed[trimmed.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return trimmed.slice(1, -1).trim();
    }
  }
  return trimmed;
}

function getTerminalFileName(section: MarkdownSection, index: number) {
  const base = section.title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${base || `section-${index + 1}`}.md`;
}

export function getTerminalFiles(sections: MarkdownSection[]): TerminalFile[] {
  return sections.map((section, index) => {
    const name = getTerminalFileName(section, index);
    return {
      aliases: [name, section.title, `${section.title}.md`, String(index + 1)].map(alias => alias.toLowerCase()),
      name,
      section
    };
  });
}

export function getTerminalTarget(link: ProfileLink) {
  const resolved = resolveProfileLink(link);
  if (resolved.action === "copy") return `clipboard:${resolved.copyText || link.value}`;
  if (!resolved.href) return link.value;
  if (resolved.href.startsWith("mailto:")) return resolved.href;

  try {
    const url = new URL(resolved.href);
    url.search = "";
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return resolved.href;
  }
}

function findTerminalLink(links: ProfileLink[], argument: string) {
  const normalized = normalizeArgument(argument).replace(/^\.\/links\//i, "").replace(/^links\//i, "");
  const number = Number.parseInt(normalized, 10);
  if (/^\d+$/.test(normalized) && number > 0) return links[number - 1];
  const label = normalized.toLowerCase();
  return links.find(link => link.label.toLowerCase() === label);
}

function getTreeTarget(argument: string) {
  const paths = argument
    .split(/\s+/)
    .filter(Boolean)
    .filter(token => token !== "-a" && token !== "--all");
  return paths.length === 0 ? "." : normalizeArgument(paths.join(" "));
}

function renderTerminalTree(files: TerminalFile[], links: ProfileLink[], target: string): TerminalCommandResult {
  const normalizedTarget = target.replace(/\/+$/, "").toLowerCase() || ".";
  const isLinksTree = normalizedTarget === "links" || normalizedTarget === "./links";

  if (isLinksTree) {
    return { kind: "links-tree", links };
  }

  if (normalizedTarget !== "." && normalizedTarget !== "./" && normalizedTarget !== "~") {
    return { kind: "lines", tone: "error", lines: [`tree: ${target}: No such directory`] };
  }

  return { files: files.map(file => file.name), kind: "root-tree", links };
}

export function executeTerminalCommand(command: string, context: TerminalCommandContext): TerminalExecution {
  const input = command.trim();
  const firstSpace = input.indexOf(" ");
  const name = (firstSpace === -1 ? input : input.slice(0, firstSpace)).toLowerCase();
  const argument = firstSpace === -1 ? "" : input.slice(firstSpace + 1).trim();
  const files = getTerminalFiles(context.model.sections);
  const handle = context.model.profile.handle || "guest";

  if (name === "help") {
    return { result: { kind: "lines", lines: [
      "Available commands:",
      "  whoami / intro / tags     read identity",
      "  ls / tree / cat <file>    explore sections",
      "  links / open <number>     explore links",
      "  pwd / uname -a / history  inspect session",
      "  clear                     clear command output",
      "Tip: use ↑/↓ for history and Tab to complete."
    ] } };
  }

  if (name === "whoami") {
    return { result: { kind: "lines", lines: [context.model.profile.displayName || "guest"] } };
  }

  if (name === "intro") {
    return { result: { kind: "lines", lines: [context.model.profile.oneLineIdentity || "No short intro yet"] } };
  }

  if (name === "tags") {
    return { result: { kind: "lines", lines: context.model.profile.tags.length > 0 ? context.model.profile.tags : ["(no tags)"] } };
  }

  if (name === "pwd") {
    return { result: { kind: "lines", lines: [`/home/${handle}`] } };
  }

  if (name === "uname") {
    return { result: { kind: "lines", lines: [`30s.to profileOS 1.0 web @${handle}`] } };
  }

  if (name === "ls") {
    if (argument.toLowerCase() === "links" || argument.toLowerCase() === "./links") {
      return { result: { kind: "lines", lines: context.links.length > 0
        ? context.links.map((link, index) => `${String(index + 1).padStart(2, "0")}  ${link.label}`)
        : ["(empty)"] } };
    }
    const lines = files.map(file => file.name);
    if (context.links.length > 0) lines.push("links/");
    return { result: { kind: "lines", lines: lines.length > 0 ? lines : ["(empty)"] } };
  }

  if (name === "tree") {
    const target = getTreeTarget(argument);
    return { result: renderTerminalTree(files, context.links, target) };
  }

  if (name === "links") {
    return { result: { kind: "lines", lines: context.links.length > 0
      ? context.links.map((link, index) => `${index + 1}  ${link.label} -> ${getTerminalTarget(link)}`)
      : ["(no links)"] } };
  }

  if (name === "cat") {
    const target = normalizeArgument(argument).replace(/^\.\//, "");
    if (!target) return { result: { kind: "lines", tone: "error", lines: ["usage: cat <file>"] } };
    if (target.toLowerCase() === "/etc/motd" || target.toLowerCase() === "etc/motd") {
      return { result: { kind: "motd", handle } };
    }
    if (/^links\//i.test(target)) {
      const link = findTerminalLink(context.links, target);
      return link
        ? { result: { kind: "lines", lines: [`${link.label} -> ${getTerminalTarget(link)}`] } }
        : { result: { kind: "lines", tone: "error", lines: [`cat: ${target}: No such file`] } };
    }
    const normalizedTarget = target.toLowerCase();
    const file = files.find(candidate => candidate.aliases.includes(normalizedTarget));
    return file
      ? { result: { kind: "markdown", nodes: file.section.nodes } }
      : { result: { kind: "lines", tone: "error", lines: [`cat: ${target}: No such file`] } };
  }

  if (name === "open") {
    if (!argument) return { result: { kind: "lines", tone: "error", lines: ["usage: open <link number>"] } };
    const link = findTerminalLink(context.links, argument);
    if (!link) return { result: { kind: "lines", tone: "error", lines: [`open: ${argument}: Link not found`] } };
    const action = resolveProfileLink(link).action === "copy" ? "Copying" : "Opening";
    return { link, result: { kind: "lines", tone: "status", lines: [`${action} ${link.label}...`] } };
  }

  if (name === "history") {
    return { result: { kind: "lines", lines: context.history.map((item, index) => `${String(index + 1).padStart(3, " ")}  ${item}`) } };
  }

  if (name === "clear") return { result: { kind: "clear" } };

  return { result: { kind: "lines", tone: "error", lines: [`command not found: ${name || input}`, "Type 'help' for available commands."] } };
}

export function TerminalLinksTree({
  interactiveLinks,
  links,
  onLinkClick,
  t
}: {
  interactiveLinks: boolean;
  links: ProfileLink[];
  onLinkClick?: (link: ProfileLink) => void;
  t: ThemeRendererProps["t"];
}) {
  return (
    <>
      <div className="term-tree-root" aria-hidden="true">./links</div>
      <nav className="term-links" aria-label={t("theme.connect")}>
        {links.map((link, index) => (
          <ProfileLinkItem
            key={link.id}
            link={link}
            interactiveLinks={interactiveLinks}
            onLinkClick={onLinkClick}
            className="term-link"
          >
            <span className="term-tree-branch" aria-hidden="true">{index === links.length - 1 ? "└──" : "├──"}</span>
            <span className="term-tree-entry">
              <span className="term-link-label">{link.label}</span>
              <span className="term-link-arrow" aria-hidden="true"> -&gt; </span>
              <span className="term-link-target">{getTerminalTarget(link)}</span>
            </span>
          </ProfileLinkItem>
        ))}
      </nav>
      <div className="term-tree-summary" aria-hidden="true">0 directories, {links.length} files</div>
    </>
  );
}

function TerminalRootTree({
  files,
  interactiveLinks,
  links,
  onLinkClick,
  t
}: {
  files: string[];
  interactiveLinks: boolean;
  links: ProfileLink[];
  onLinkClick?: (link: ProfileLink) => void;
  t: ThemeRendererProps["t"];
}) {
  const directoryCount = links.length > 0 ? 1 : 0;
  const fileCount = files.length + links.length;

  return (
    <>
      <div className="term-tree-root" aria-hidden="true">.</div>
      <div className="term-root-tree">
        {files.map((file, index) => (
          <div className="term-tree-row term-tree-file-row" key={`${index}-${file}`}>
            <span className="term-tree-branch" aria-hidden="true">
              {links.length === 0 && index === files.length - 1 ? "└──" : "├──"}
            </span>
            <span className="term-tree-entry term-tree-file-name">{file}</span>
          </div>
        ))}
        {links.length > 0 && (
          <>
            <div className="term-tree-row term-tree-directory-row">
              <span className="term-tree-branch" aria-hidden="true">└──</span>
              <span className="term-tree-entry term-tree-directory-name">links/</span>
            </div>
            <nav className="term-root-tree-links" aria-label={t("theme.connect")}>
              {links.map((link, index) => (
                <ProfileLinkItem
                  key={link.id}
                  link={link}
                  interactiveLinks={interactiveLinks}
                  onLinkClick={onLinkClick}
                  className="term-link term-root-tree-link"
                >
                  <span className="term-tree-branch" aria-hidden="true">{index === links.length - 1 ? "└──" : "├──"}</span>
                  <span className="term-tree-entry">
                    <span className="term-link-label">{link.label}</span>
                    <span className="term-link-arrow" aria-hidden="true"> -&gt; </span>
                    <span className="term-link-target">{getTerminalTarget(link)}</span>
                  </span>
                </ProfileLinkItem>
              ))}
            </nav>
          </>
        )}
      </div>
      <div className="term-tree-summary" aria-hidden="true">
        {directoryCount} {directoryCount === 1 ? "directory" : "directories"}, {fileCount} {fileCount === 1 ? "file" : "files"}
      </div>
    </>
  );
}

function TerminalResult({
  interactiveLinks,
  onLinkClick,
  result,
  showAttribution,
  t
}: {
  interactiveLinks: boolean;
  onLinkClick?: (link: ProfileLink) => void;
  result: TerminalCommandResult;
  showAttribution: boolean;
  t: ThemeRendererProps["t"];
}) {
  if (result.kind === "clear") return null;
  if (result.kind === "links-tree") {
    return (
      <TerminalLinksTree
        interactiveLinks={interactiveLinks}
        links={result.links}
        onLinkClick={onLinkClick}
        t={t}
      />
    );
  }
  if (result.kind === "root-tree") {
    return (
      <TerminalRootTree
        files={result.files}
        interactiveLinks={interactiveLinks}
        links={result.links}
        onLinkClick={onLinkClick}
        t={t}
      />
    );
  }
  if (result.kind === "markdown") {
    return <MarkdownBody className="term-output term-session-result" nodes={result.nodes} />;
  }
  if (result.kind === "motd") {
    if (!showAttribution) {
      return <div className="term-output term-session-result term-session-motd">30s.to/@{result.handle}</div>;
    }
    return (
      <div className="term-output term-session-result term-session-motd">
        Welcome to 30s.to/@{result.handle} (<ThemeAttribution t={t} />)
      </div>
    );
  }
  const toneClass = result.tone ? ` term-session-result-${result.tone}` : "";
  return (
    <div className={`term-output term-session-result${toneClass}`}>
      {result.lines.map((line, index) => <div key={`${index}-${line}`}>{line || " "}</div>)}
    </div>
  );
}

export function TerminalSession({
  interactiveLinks,
  links,
  model,
  onLinkClick,
  showAttribution,
  t
}: {
  interactiveLinks: boolean;
  links: ProfileLink[];
  model: ProfileRenderModel;
  onLinkClick?: (link: ProfileLink) => void;
  showAttribution: boolean;
  t: ThemeRendererProps["t"];
}) {
  const [entries, setEntries] = useState<TerminalEntry[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const entryIdRef = useRef(0);
  const hasInteractedRef = useRef(false);
  const labelId = useId();
  const files = useMemo(() => getTerminalFiles(model.sections), [model.sections]);
  const completions = useMemo(() => [
    ...COMMANDS,
    ...files.map(file => `cat ${file.name}`),
    ...links.map((_, index) => `open ${index + 1}`)
  ], [files, links]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    const input = inputRef.current;
    const body = input?.closest<HTMLElement>(".term-body");
    if (!input) return;
    const behavior: ScrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    const frame = window.requestAnimationFrame(() => {
      if (body && body.scrollHeight > body.clientHeight + 1) {
        body.scrollTo({ top: body.scrollHeight, behavior });
      } else {
        input.scrollIntoView({ block: "nearest", behavior });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [entries]);

  const runCommand = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (composingRef.current) return;
    const command = value.trim();
    if (!command) return;

    const nextHistory = [...history, command].slice(-50);
    const execution = executeTerminalCommand(command, { history: nextHistory, links, model });
    hasInteractedRef.current = true;
    setHistory(nextHistory);
    setHistoryIndex(nextHistory.length);
    setValue("");

    if (execution.result.kind === "clear") {
      setEntries([]);
    } else {
      entryIdRef.current += 1;
      const entry = { command, id: entryIdRef.current, result: execution.result };
      setEntries(current => [...current, entry].slice(-50));
    }

    const linkToOpen = execution.link;
    if (linkToOpen && interactiveLinks) {
      window.requestAnimationFrame(() => onLinkClick?.(linkToOpen));
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || composingRef.current) return;
    if (event.key === "Escape") {
      event.currentTarget.blur();
      return;
    }
    if (event.key === "Tab" && !event.shiftKey && value.trim()) {
      const normalized = value.toLowerCase();
      const completion = completions.find(candidate => candidate.toLowerCase().startsWith(normalized));
      if (completion) {
        event.preventDefault();
        setValue(completion);
      }
      return;
    }
    if ((event.key !== "ArrowUp" && event.key !== "ArrowDown") || history.length === 0) return;
    event.preventDefault();
    const nextIndex = event.key === "ArrowUp"
      ? Math.max(0, historyIndex - 1)
      : Math.min(history.length, historyIndex + 1);
    setHistoryIndex(nextIndex);
    setValue(nextIndex === history.length ? "" : history[nextIndex]);
  };

  return (
    <>
      <div
        className="term-session-log"
        role="log"
        aria-label={t("theme.terminal.outputLabel")}
        aria-live="polite"
        aria-relevant="additions text"
      >
        {entries.map(entry => (
          <div className="term-block term-session-entry" key={entry.id}>
            <div className="term-prompt term-command-echo"><span>~ $</span>{entry.command}</div>
            <TerminalResult
              interactiveLinks={interactiveLinks}
              onLinkClick={onLinkClick}
              result={entry.result}
              showAttribution={showAttribution}
              t={t}
            />
          </div>
        ))}
      </div>
      <form className="term-prompt term-command-line" onSubmit={runCommand}>
        <label className="term-command-label" htmlFor={labelId}>{t("theme.terminal.commandLabel")}</label>
        <span aria-hidden="true">~ $</span>
        {!isFocused && !value && <span className="term-command-idle-cursor" aria-hidden="true" />}
        <input
          ref={inputRef}
          id={labelId}
          className="term-command-input"
          value={value}
          aria-label={t("theme.terminal.commandLabel")}
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          enterKeyHint="send"
          spellCheck={false}
          onChange={event => {
            setValue(event.target.value);
            setHistoryIndex(history.length);
          }}
          onCompositionStart={() => { composingRef.current = true; }}
          onCompositionEnd={() => { composingRef.current = false; }}
          onBlur={() => { setIsFocused(false); }}
          onFocus={() => { setIsFocused(true); }}
          onKeyDown={handleKeyDown}
        />
        <button className="term-command-submit" type="submit" tabIndex={-1}>{t("theme.terminal.commandSubmit")}</button>
      </form>
    </>
  );
}
